import { take } from 'rxjs/operators';
import { from, switchMap, forkJoin } from 'rxjs';
import { ChatHistoryHelper } from './chat-history.helper';

/**
 * SendInputMessageHelper
 *
 * Extracted from ChatbotexpandComponent. Holds `sendInputMessage`, which pushes the
 * question block for instant UI feedback, resets state, and defers the heavy
 * orchestration to `continueProcessingMessage` (see OrchestrationHelper).
 *
 * Follows the existing `comp: any` helper pattern — the component keeps a thin wrapper
 * method (bound in the template) that delegates here, so all call sites remain unchanged
 * and behavior is preserved (verbatim `this.` -> `comp.` transcription).
 */
export class SendInputMessageHelper {
    static sendInputMessage(comp: any, ChatResponse?: any, isChatResponse: boolean = false, targetBlock: any = null): void {
        comp.streamStatusText = '';
        comp.currentView = 'chat';
        if (comp.isNewChatStarted && !isChatResponse && comp.abandoningSessionId) {
            comp.isNewChatStarted = false;
            const sessionToDelete = comp.abandoningSessionId;
            from(comp.getBearerToken()).pipe(
                switchMap((token: any) => forkJoin([
                    comp.apiService.deleteRestrictedCache(token, sessionToDelete, true),
                    comp.apiService.deleteClientStudyContext(token, sessionToDelete)
                ]))
            ).subscribe({
                next: () => {
                    comp.abandoningSessionId = '';
                },
                error: (err: any) => console.error('[ChatbotService] Error deleting abandoned session cache/context:', err)
            });
        }

        if (!isChatResponse)
            comp.selectedChatId = null;
        comp.moduleSelectionToggle = false;
        comp.showModuleDropdown = false;
        comp.askMeLanding = false;

        if (!isChatResponse && comp.isInsightsStreaming) {
            console.warn('[SEND GUARD] Cannot send input - stream is active');
            return;
        }

        // 1. PUSH QUESTION IMMEDIATELY FOR INSTANT UI FEEDBACK
        if (!isChatResponse && comp.inputValue && comp.inputValue.trim()) {
            comp.isHistorySaving = true;
            comp.isWaitingForFinalSave = true;
            // Remove any trailing failed question from the UI before pushing the new one
            if (comp.chatPreviewBlocks.length > 0) {
                const lastBlock = comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1];
                if (lastBlock.failedCase) {
                    comp.chatPreviewBlocks.pop();
                    comp.chatResponse = []; // Clear pending payload so it isn't saved to history
                }
            }

            comp.resetSessionState(); // Reset global component state for fresh question
            const tempId = ChatHistoryHelper.generateGuid();
            comp.chatPreviewBlocks.push({
                question: comp.inputValue,
                messageId: tempId,
                isTemp: true,
                response: '',
                isLiked: null,
                isStarred: false,
                feedbackSaved: false,
                showLikeInput: false,
                likeReason: '',
                showDislikeInput: false,
                dislikeReason: '',
                QuestionAnswer: comp.defaultQuestionAnswer,
                isTrendActive: comp.isTrendActive,
                isExportspopup: false,
                sampleSizeDropdown: false,
                confidenceLevelDropDown: false,
                referenceLevelDropDown: false,
                baseDropDown: false,
                weightDropDown: false,
                isReference: false,
                isSig: true,
                isIndex: false,
                DefaultSampleSize: 'None',
                SignificanceVsReference: '',
                DefaultConfidenceLevel: '95%',
                DefaultReferenceLevel: 'None',
                selectedWeight: comp.selectedWeight || 'Weight',
                DefaultWeight: comp.DefaultWeight || 'Total Population',
                DefaultBase: comp.DefaultBase || 'Total Base',
                isTotalBase: true,
                isAnswerBase: true,
                is_sigtesting: true,
                is_Time_Period_Present: true,
                isTranspose: false,
                dataDropDown: false,
                DefaultDataDropDown: 'Data',
                isTableClick: true,
                isVerticalStackChartClick: false,
                isVerticalBarChartClick: false,
                isVerticalGroupBarChartClick: false,
                isLineChartClick: false,
                isChartView: true,
                disableStackChart: false,
                disableLineChart: false,
                showStackTooltip: false,
                showLineTooltip: false,
                showBarTooltip: false,
                showMainTooltip: false,
                StackFlag: false,
                displayTable: true,
                isTableClosing: false,
                isChartClosing: false,
                // Isolated Chart Objects
                StackChart: { title: '', Data: [], Properties: [] },
                LineChart: { title: '', Data: [], Properties: [] },
                BarChart: { title: '', Data: [], Properties: [] },
                // Isolated Data Storage
                OutputdataObject: { Row: [], Column: [], RowNesting: [], ColNesting: [], Filter: [], CustomGroup: [], CustomCalculation: [] },
                CrossTabOutPutData: []
            });
            // Capture input for orchestration and clear immediately for UX
            const submittedQuery = comp.inputValue;
            comp.currentQueryForStream = submittedQuery;
            comp.hasPendingQuery = true; // Flag to ensure the flow continues
            comp.selectedMessageId = null; // Clear selected message so empty UI isn't shown
            comp.inputValue = '';
            comp.scrollSpacerHeight = 0;
            comp.cdr.detectChanges();

            // Instantly trigger scroll to show the question
            comp.scrollToBottom();
        }

        if (isChatResponse) {
            comp.isInsightsStreaming = false; // Not streaming when loading history
        } else {
            comp.isInsightsStreaming = true;
            comp.availableViews = [];
            comp.activeView = null;
            comp.orchestrationRawData = null;
            comp.allMemberResponses = null;
            comp.setFailedCaseState(false, targetBlock);
            comp.noContentFlag = false;
        }

        if (comp.hasPendingQuery || isChatResponse) {
            comp.hasPendingQuery = false;
            comp.displayTable = false;
            comp.showSnapshot = false;
            comp.isCreateBtnClick = false;
            comp.is_indexing = false;
            comp.is_sigtesting = false;
            comp.insightsVal = '';
            comp.insightsStreamingText = '';
            comp.insightsStreamingPoints = [];
            comp.isselectionsummarycollapse = false;
            comp.isselectionsummarycollapse2 = false;
            comp.OutputdataObject.Row = [];
            comp.OutputdataObject.Column = [];

            // DEFER THE HEAVY LOADING AND ORCHESTRATION TO ALLOW BROWSER PAINT
            const delay = (!isChatResponse && comp.chatPreviewBlocks.filter((b: any) => b.question).length > 1) ? 500 : 0;
            setTimeout(() => {
                comp.displayTable = false;
                comp.showSnapshot = false;
                comp.isCreateBtnClick = false;
                comp.is_indexing = false;
                comp.is_sigtesting = false;
                comp.insightsVal = '';
                comp.insightsStreamingText = '';
                comp.insightsStreamingPoints = [];
                comp.MainResult = undefined;
                comp.OutputData = [];
                comp.CrossTabOutPutData = [];
                comp._configService.updateSignificanceValue('None');
                comp._configService.updateConfedenceValue('None');
                comp._configService.IndexSelected = false;
                comp._configService.baseSelected = 'Total Base';
                comp._configService.isTotalBase = true;
                comp.arktabaccess = true;
                comp.isStreamFinished = false;
                comp.startAiLoader();

                comp.isVerticalGroupBarChartClick = false;
                comp.isVerticalBarChartClick = false;
                comp.isVerticalStackChartClick = false;
                comp.isLineChartClick = false;
                comp.isInsightspopupOpen = false;

                comp.continueProcessingMessage(ChatResponse, isChatResponse, targetBlock);
            }, delay);
        }
    }

  static processSendMessageRequest(comp: any): void {
    comp.subscriptions.add(
      comp.sharedChatService.sendMessageRequest$.pipe(take(1)).subscribe((payload: any) => {
        if (payload && payload.inputValue && payload.inputValue.trim() !== '') {
          // Guard: Prevent sending new message while previous question is still processing
          if (comp.arktabaccess) {
            console.warn('[SEND GUARD] Cannot send message from landing page - previous question still processing');
            comp.sharedChatService.clearRequest();
            return;
          }

          comp.inputValue = payload.inputValue.trim();
          comp.selectedMessageId = null;
          if (payload.guidChatId) {
            comp.guidChatId = payload.guidChatId;
            comp.validatedSessionId = payload.guidChatId;
          } else {
            console.warn('[RECEIVE-MESSAGE] Fallback triggered: No guidChatId in payload!');
          }
          if (payload.modulesAccess) {
            comp.modulesAccess = payload.modulesAccess;
          }
          comp.sendInputMessage();
          comp.sharedChatService.clearRequest();
        } else {
          if (payload) {
            comp.landingdata = {
              ...comp.landingdata,
              client_code: payload.clientCode,
              study: payload.study,
              dataArray: payload.dataArray || [],
              landingData: payload.landingData || {},
              guidChatId: payload.guidChatId,
              modules_access: payload.modulesAccess
            };
            if (payload.guidChatId) {
              comp.guidChatId = payload.guidChatId;
              comp.validatedSessionId = payload.guidChatId;
            } else {
              console.warn('[RECEIVE-MESSAGE] (Fallback Block) Fallback triggered: No guidChatId in payload!');
            }
            comp.selected_study = payload.study;
            comp.selected_client = payload.clientCode;
            comp.loadData = payload.dataArray || [];
            comp.leftPanelData = payload.landingData || {};
          }
          comp.arktabaccess = false;
          comp.currentView = 'landing';
          comp.chatPreviewBlocks = [];
          comp.isNewChatSession = true;
        }
      })
    );
  }
}
